import React from "react";
import {
  Box, Container, Heading, Text, VStack, SimpleGrid, Button,
  HStack, Icon, Badge, Divider
} from "@chakra-ui/react";
import {
  FaCar, FaCheckCircle, FaArrowRight, FaPhone,
  FaTools, FaShip, FaBriefcase, FaMotorcycle
} from "react-icons/fa";
import { Link as RouterLink } from "react-router-dom";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";

const FINANCE_PRIMARY = "#141C49";
const FINANCE_ACCENT = "#C2A878";
const NAVY = "#141C49";

const assetTypes = [
  {
    icon: FaCar,
    title: "Car Loans",
    description: "New or used, personal or business — we find competitive car finance across secured, unsecured, and bad credit options with fixed repayments and optional balloon payments.",
    badge: "Most Popular",
    features: ["New & used vehicles", "Fixed repayments", "Balloon payment options", "Secured & unsecured"]
  },
  {
    icon: FaShip,
    title: "Caravan & Boats",
    description: "Finance your caravan, boat, jet ski, or any recreational asset. We work with lenders who understand lifestyle finance and can structure it to suit your cashflow.",
    badge: "Lifestyle",
    features: ["Caravans & motorhomes", "Boats & jet skis", "Recreational vehicles", "Competitive rates"]
  },
  {
    icon: FaTools,
    title: "Equipment Finance",
    description: "From tradie tools to heavy machinery — we help businesses and tradespeople finance the equipment they need to operate and grow, without draining working capital.",
    badge: "Business",
    features: ["Machinery & plant", "Tradie equipment", "Medical equipment", "Technology assets"]
  },
  {
    icon: FaBriefcase,
    title: "Business Asset Finance",
    description: "Chattel mortgages, low-doc loans, novated leases, and business car leases — including options for new ABN holders. We match the structure to your business situation.",
    badge: "Business",
    features: ["Chattel mortgages", "Business car leases", "Low-doc options", "New ABN holders welcome"]
  }
];

const steps = [
  { number: "01", title: "Tell us what you need", desc: "Share what asset you're looking to finance — personal or business. We take it from there." },
  { number: "02", title: "We find the right structure", desc: "Secured or unsecured, chattel mortgage or lease — we match the finance structure to your situation." },
  { number: "03", title: "Compare lenders", desc: "We search across specialist asset finance lenders to find you the most competitive rate and terms." },
  { number: "04", title: "Fast approval", desc: "Asset finance approvals are typically fast. Many clients receive approval within 24–48 hours." },
  { number: "05", title: "Funds released", desc: "Once approved, funds are released directly to the vendor. You get the keys — or the equipment." },
  { number: "06", title: "Ongoing support", desc: "We're available throughout the life of your loan. Need to refinance or upgrade? We're here." }
];

const FinanceServicesAssets: React.FC = () => {
  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/finance/services/assets"
        fallbackTitle="Asset Finance - Car, Equipment & More | One Group Finance"
        fallbackDescription="Car loans, caravan finance, equipment and business asset finance. Fast approvals, competitive rates, personal and business solutions."
      />
      <NavbarWithCallToAction />

      {/* Hero */}
      <Box
        position="relative"
        color="white"
        py={{ base: 20, md: 28 }}
        backgroundImage="url('/assets/finance-assets/asset 19.jpeg')"
        backgroundSize="cover"
        backgroundPosition="center"
        backgroundAttachment={{ base: "scroll", md: "fixed" }}
        _before={{
          content: '""',
          position: "absolute",
          top: 0, left: 0, right: 0, bottom: 0,
          bg: "rgba(0, 31, 63, 0.85)",
          zIndex: 0
        }}
      >
        <Container maxW="container.xl" position="relative" zIndex={1}>
          <VStack spacing={6} textAlign="center">
            <Badge bg={FINANCE_ACCENT} color="white" fontSize="sm" px={4} py={1} borderRadius="full">
              Asset Finance
            </Badge>
            <Heading size="2xl" fontWeight="bold" maxW="3xl">
              Finance for the Things That Move You
            </Heading>
            <Text fontSize="xl" color="gray.300" maxW="2xl">
              From your next car to business equipment — we help you finance the assets that keep your life and business moving forward.
            </Text>
            <HStack spacing={4} pt={4} flexWrap="wrap" justify="center">
              <Button
                as={RouterLink}
                to="/finance/contact"
                size="lg"
                bg="white"
                color={NAVY}
                rightIcon={<FaArrowRight />}
                _hover={{ bg: FINANCE_ACCENT, color: "white" }}
                transition="all 0.2s"
              >
                Get a Quote
              </Button>
              <Button
                size="lg"
                variant="outline"
                borderColor="white"
                color="white"
                leftIcon={<FaPhone />}
                _hover={{ bg: "rgba(255,255,255,0.1)" }}
                transition="all 0.2s"
              >
                1300 536 159
              </Button>
            </HStack>
          </VStack>
        </Container>
      </Box>

      {/* Intro */}
      <Box bg="gray.50" py={16}>
        <Container maxW="container.xl">
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={12} alignItems="center">
            <Box
              h="380px"
              borderRadius="2xl"
              overflow="hidden"
              shadow="xl"
              backgroundImage="url('/assets/finance-assets/asset 12.jpeg')"
              backgroundSize="cover"
              backgroundPosition="center"
            />
            <VStack align="flex-start" spacing={5}>
              <Text fontSize="sm" color={FINANCE_ACCENT} textTransform="uppercase" letterSpacing="wider" fontWeight="600">
                Simple. Flexible. Tailored to you.
              </Text>
              <Heading size="xl" color={NAVY}>
                Whatever You've Got in Mind, We Can Finance It
              </Heading>
              <Text color="gray.600" fontSize="lg" lineHeight="tall">
                We partner with Ausloans — Australia's largest privately-owned finance group — giving us access to 40+ lenders specialising in asset finance. That means better rates, faster approvals, and the right structure for your situation.
              </Text>
              <Text color="gray.600" fontSize="lg" lineHeight="tall">
                Whether it's a car for the family, a caravan for the adventures, or heavy equipment for your business — we handle the paperwork and you focus on what matters.
              </Text>
              <SimpleGrid columns={2} spacing={3} w="full">
                {[
                  "No obligation quote",
                  "No impact on credit score",
                  "Fast 24–48hr approvals",
                  "Personal & business options"
                ].map((item) => (
                  <HStack key={item} spacing={2}>
                    <Icon as={FaCheckCircle} color={FINANCE_ACCENT} flexShrink={0} />
                    <Text fontSize="sm" color="gray.700">{item}</Text>
                  </HStack>
                ))}
              </SimpleGrid>
            </VStack>
          </SimpleGrid>
        </Container>
      </Box>

      {/* Asset Types */}
      <Container maxW="container.xl" py={20}>
        <VStack spacing={4} textAlign="center" mb={12}>
          <Heading size="xl" color="gray.800">What We Can Finance</Heading>
          <Text fontSize="lg" color="gray.600" maxW="2xl">
            Personal or business — we have asset finance solutions for every situation.
          </Text>
        </VStack>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
          {assetTypes.map((asset, idx) => (
            <Box
              key={idx}
              p={8}
              bg="white"
              border="1px solid"
              borderColor="gray.200"
              borderRadius="xl"
              _hover={{ shadow: "xl", borderColor: FINANCE_ACCENT, transform: "translateY(-4px)" }}
              transition="all 0.3s"
            >
              <HStack justify="space-between" mb={5}>
                <Icon as={asset.icon} boxSize={10} color={FINANCE_ACCENT} />
                <Badge bg={FINANCE_ACCENT} color="white" fontSize="xs">{asset.badge}</Badge>
              </HStack>
              <Heading size="md" mb={3} color="gray.800">{asset.title}</Heading>
              <Text color="gray.600" fontSize="sm" lineHeight="tall" mb={5}>{asset.description}</Text>
              <Divider mb={4} />
              <SimpleGrid columns={2} spacing={2}>
                {asset.features.map((f) => (
                  <HStack key={f} spacing={2}>
                    <Icon as={FaCheckCircle} color={FINANCE_ACCENT} boxSize={3} flexShrink={0} />
                    <Text fontSize="xs" color="gray.600">{f}</Text>
                  </HStack>
                ))}
              </SimpleGrid>
            </Box>
          ))}
        </SimpleGrid>
      </Container>

      {/* Process */}
      <Box bg="gray.50" py={20}>
        <Container maxW="container.xl">
          <VStack spacing={4} textAlign="center" mb={12}>
            <Heading size="xl" color={NAVY}>How It Works</Heading>
            <Text fontSize="lg" color="gray.600" maxW="xl">Fast, straightforward, and handled by us.</Text>
          </VStack>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
            {steps.map((step) => (
              <Box
                key={step.number}
                p={8}
                bg="white"
                border="1px solid"
                borderColor="gray.200"
                borderRadius="xl"
                position="relative"
                _hover={{ shadow: "lg", borderColor: FINANCE_ACCENT }}
                transition="all 0.3s"
              >
                <Text
                  fontSize="4xl"
                  fontWeight="900"
                  color={FINANCE_ACCENT}
                  opacity={0.3}
                  position="absolute"
                  top={4}
                  right={6}
                  lineHeight={1}
                >
                  {step.number}
                </Text>
                <Heading size="md" mb={3} color={NAVY}>{step.title}</Heading>
                <Text color="gray.600" fontSize="sm" lineHeight="tall">{step.desc}</Text>
              </Box>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* CTA */}
      <Box bg={NAVY} color="white" py={20}>
        <Container maxW="container.xl" textAlign="center">
          <Heading size="xl" mb={4}>Ready to Finance Your Next Asset?</Heading>
          <Text fontSize="lg" mb={8} color="gray.300" maxW="xl" mx="auto">
            Get a no-obligation quote today. Fast approvals, competitive rates, handled by people who care.
          </Text>
          <HStack justify="center" spacing={4} flexWrap="wrap">
            <Button
              as={RouterLink}
              to="/finance/contact"
              size="lg"
              bg={FINANCE_ACCENT}
              color="white"
              rightIcon={<FaArrowRight />}
              _hover={{ bg: "#a8905f" }}
              transition="all 0.2s"
            >
              Get a Free Quote
            </Button>
            <Button
              size="lg"
              variant="outline"
              borderColor="white"
              color="white"
              leftIcon={<FaPhone />}
              _hover={{ bg: "rgba(255,255,255,0.1)" }}
              transition="all 0.2s"
            >
              1300 536 159
            </Button>
          </HStack>
        </Container>
      </Box>

      <FooterWithFourColumns />
    </Box>
  );
};

export default FinanceServicesAssets;
